import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, ButtonProps, Center } from '@chakra-ui/react';
import { UserSignupEmailCaptureFormProps } from '@src/components/sco/Account/UserSignupEmailCaptureForm';
import type { PaywallGateDensity } from '@src/components/sco/Conversion/PaywallGate';
import { ConversionGate } from '@src/components/sco/Conversion/controller/ConversionGate';
import type { PaywallIntentId } from '@src/components/sco/Conversion/intent';
import { conversionForPermission, type ContentPermissionKey } from '@src/components/sco/Conversion/permissions';
import { BoxedCardCTA } from '@src/components/sco/UserAccessGate/cta-variants/BoxedCardCTA';
import { BoxedCardOverImageCTA } from '@src/components/sco/UserAccessGate/cta-variants/BoxedCardOverImageCTA';
import { ButtonCTA } from '@src/components/sco/UserAccessGate/cta-variants/ButtonCTA';
import { InlineCardCTA } from '@src/components/sco/UserAccessGate/cta-variants/InlineCardCTA';
import { PageOverlayCTA } from '@src/components/sco/UserAccessGate/cta-variants/PageOverlayCTA';
import { UserAccessCheckOptions, UserAccessRules, useUserAccessCheck } from '@src/hooks/useUserAccessCheck';
import { ReactNode, useState } from 'react';
const CTA_VARIANTS = {
  'boxed-card': BoxedCardCTA,
  'boxed-card-centered': props => <Center height={'full'} width={'full'}>
      <BoxedCardCTA {...props} />
    </Center>,
  'boxed-card-on-image': BoxedCardOverImageCTA,
  'button-only': ButtonCTA,
  'inline-card': InlineCardCTA,
  'page-overlay': PageOverlayCTA,
  'video-overlay': BoxedCardOverImageCTA
};
export type UserAccessGateCTAProps = {
  bgImageUrl?: string;
  boxProps?: BoxProps;
  buttonProps?: ButtonProps & {
    href?: string;
  };
  buttons?: Array<ButtonProps & {
    href?: string;
  }>;
  children?: ReactNode;
  hideLoginLink?: boolean;
  hideLoginPopover?: boolean;
  icon?: any;
  imageUrl?: string;
  isDismissible?: boolean;
  emailCaptureProps?: UserSignupEmailCaptureFormProps;
  subtitle?: ReactNode;
  title?: ReactNode;
  showEmailCapture?: boolean;
  trackingId?: string;
  wrapperProps?: BoxProps;
};
const PAID_DENSITY: Record<string, PaywallGateDensity> = {
  'boxed-card': 'boxed',
  'boxed-card-centered': 'boxed',
  // Never mount the locked media (a video player) behind the wall: these render the card alone.
  'boxed-card-on-image': 'page',
  'button-only': 'button',
  'inline-card': 'boxed',
  'page-overlay': 'overlay',
  'video-overlay': 'page'
};

/**
 * The paid permission a gate is about: the first required or bypassing permission that Startups
 * Pro or Advisor Team grants. Registration-only gates return null and keep their sign-up CTA.
 */
export function paidGatePermission(options: UserAccessCheckOptions): ContentPermissionKey | null {
  const rules = [options.require, options.bypass].filter((rule): rule is UserAccessRules => Boolean(rule) && rule !== true);
  for (const rule of rules) {
    for (const permission of rule.contentPermissions || []) {
      if (conversionForPermission(permission as ContentPermissionKey)) {
        return permission as ContentPermissionKey;
      }
    }
  }
  return null;
}
export type UserAccessGateProps = UserAccessCheckOptions & {
  children?: ReactNode;
  /** Founder moment for a paid gate; defaults from the permission. */
  intent?: PaywallIntentId;
  tokens?: Record<string, string>;
  renderFailed?: () => any;
  renderPassed?: () => any;
  ctaVariant?: 'button' | 'inline-card' | 'boxed-card' | 'boxed-card-centered' | 'video-overlay' | 'page-overlay' | string;
  ctaProps?: UserAccessGateCTAProps;
};
export const UserAccessGate = props => {
  const $ = _c(37);
  const t0 = props.bypass || false;
  let t1;
  if ($[0] !== props.require) {
    t1 = props.require || {};
    $[0] = props.require;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      bypass: t0,
      require: t1
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    currentUser,
    accessCheckPassed
  } = useUserAccessCheck(t2);
  const [dismissed, setDismissed] = useState(false);
  const CTAVariantComponent = CTA_VARIANTS[props.ctaVariant];
  let t3;
  if ($[5] !== props) {
    t3 = paidGatePermission(props);
    $[5] = props;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const paidPermission = t3;
  if (!currentUser) {
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <></>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (accessCheckPassed) {
    if (props.renderPassed) {
      let t4;
      if ($[8] !== props.renderPassed) {
        t4 = props.renderPassed();
        $[8] = props.renderPassed;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      return t4;
    } else {
      let t4;
      if ($[10] !== props.children) {
        t4 = <>{props.children}</>;
        $[10] = props.children;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      return t4;
    }
  } else {
    if (props.renderFailed) {
      let t4;
      if ($[12] !== props.renderFailed) {
        t4 = props.renderFailed();
        $[12] = props.renderFailed;
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      return t4;
    } else {
      if (paidPermission && props.ctaVariant && PAID_DENSITY[props.ctaVariant]) {
        const density = dismissed ? "boxed" : PAID_DENSITY[props.ctaVariant];
        const title = props.ctaProps?.title;
        const t4 = density === "button";
        const t5 = typeof title === "string" ? title : undefined;
        let t6;
        if ($[14] !== dismissed || $[15] !== props.ctaProps?.isDismissible) {
          t6 = props.ctaProps?.isDismissible && !dismissed ? () => setDismissed(true) : undefined;
          $[14] = dismissed;
          $[15] = props.ctaProps?.isDismissible;
          $[16] = t6;
        } else {
          t6 = $[16];
        }
        const t7 = dismissed ? null : props.children;
        let t8;
        if ($[17] !== density || $[18] !== paidPermission || $[19] !== props.intent || $[20] !== props.tokens || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
          t8 = <ConversionGate compactCta={t4} density={density} headline={t5} intent={props.intent} isUnlocked={false} permission={paidPermission} tokens={props.tokens} onDismiss={t6}>{t7}</ConversionGate>;
          $[17] = density;
          $[18] = paidPermission;
          $[19] = props.intent;
          $[20] = props.tokens;
          $[21] = t4;
          $[22] = t5;
          $[23] = t6;
          $[24] = t7;
          $[25] = t8;
        } else {
          t8 = $[25];
        }
        const gate = t8;
        let t9;
        if ($[26] !== density || $[27] !== gate || $[28] !== props.ctaProps?.boxProps) {
          t9 = density === "overlay" ? gate : <Box {...props.ctaProps?.boxProps || {}}>{gate}</Box>;
          $[26] = density;
          $[27] = gate;
          $[28] = props.ctaProps?.boxProps;
          $[29] = t9;
        } else {
          t9 = $[29];
        }
        return t9;
      } else {
        if (props.ctaVariant && CTAVariantComponent) {
          let t4;
          if ($[30] !== props.ctaProps) {
            t4 = props.ctaProps || {};
            $[30] = props.ctaProps;
            $[31] = t4;
          } else {
            t4 = $[31];
          }
          let t5;
          if ($[32] !== CTAVariantComponent || $[33] !== props.children || $[34] !== t4) {
            t5 = <CTAVariantComponent {...t4}>{props.children}</CTAVariantComponent>;
            $[32] = CTAVariantComponent;
            $[33] = props.children;
            $[34] = t4;
            $[35] = t5;
          } else {
            t5 = $[35];
          }
          return t5;
        } else {
          let t4;
          if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
            t4 = <></>;
            $[36] = t4;
          } else {
            t4 = $[36];
          }
          return t4;
        }
      }
    }
  }
};
export const UserAccessGates = props => {
  const $ = _c(4);
  let t0;
  if ($[0] !== props.gates) {
    t0 = props.gates.map(_temp);
    $[0] = props.gates;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <>{t0}</>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
function _temp(gateProps, idx) {
  return <UserAccessGate key={idx} {...gateProps} />;
}